import { useEffect, useState } from "react";
import axios, { API, trackEvent } from "./utils/api";

const SUPPORT_EMAIL = "noamshvetz2009@gmail.com";
const tickerFallback = ["SPY", "QQQ", "NVDA", "AAPL", "MSFT", "TSLA", "META", "AMZN"];

const features = [
  ["Market Scanner", "Rank US stock and ETF candidates using momentum, trend, price position, volume context, and available market signals."],
  ["Setup Reasons", "See why each result appeared, including score, movement, trend, risk/reward levels, and research links."],
  ["Watchlists", "Save ideas, revisit symbols, and keep your research organized across scan sessions."],
  ["Paper Trading", "Practice with a virtual balance, position history, and simple educational trade tracking."],
  ["Signal History", "Review prior scanner signals, digest repeated names, and backtest symbols for research."],
  ["Secure Access", "Google sign-in, verified email accounts, and server-side access checks — free for everyone."],
];

const included = [
  "US stock and ETF scanning",
  "Ranked research candidates",
  "Score breakdowns and setup reasons",
  "Risk/reward levels",
  "Watchlists",
  "Paper trading simulator",
  "Signal tracker",
  "Backtests for research",
  "Weekly scan digest",
  "Account management",
];

const faqs = [
  ["Is this financial advice?", "No. Trade Scanner is educational and informational. It helps users discover, rank, compare, and research candidates; users make their own decisions."],
  ["Does it guarantee profitable stocks?", "No. Markets are risky and unpredictable. Trade Scanner does not promise returns, accuracy, or profitability."],
  ["What markets are supported?", "V1 focuses on United States stocks and US ETFs only. Crypto, forex, and international markets are not included in V1."],
  ["Is Trade Scanner free?", "Yes. All features are free to use, no subscription required."],
];

function SectionTitle({ eyebrow, title, copy }) {
  return (
    <div className="section-title">
      <span>{eyebrow}</span>
      <h2>{title}</h2>
      {copy && <p>{copy}</p>}
    </div>
  );
}

function PulseDots() {
  return <span className="ts-loading-dots" aria-hidden="true"><i /><i /><i /></span>;
}

export default function Landing({ onLogin, onRegister }) {
  const [tickers, setTickers] = useState(tickerFallback.map((sym) => ({ sym, price: "--", chg: null })));
  const [contact, setContact] = useState({ name: "", email: "", subject: "Trade Scanner question", message: "" });
  const [contactStatus, setContactStatus] = useState("");

  useEffect(() => {
    trackEvent("landing_page_visit");
    let cancelled = false;
    const loadTicker = async () => {
      try {
        const res = await fetch(`${API}/api/ticker`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.tickers?.length) {
          setTickers(data.tickers.map((item) => ({
            sym: item.sym,
            price: item.price ? item.price.toFixed(2) : "--",
            chg: item.chg,
          })));
        }
      } catch {
        // Decorative ticker only.
      }
    };
    loadTicker();
    const id = setInterval(loadTicker, 120000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const submitContact = async (event) => {
    event.preventDefault();
    setContactStatus("Sending...");
    try {
      await axios.post("/api/contact", contact);
      setContactStatus("Message sent. You can also email support directly anytime.");
      setContact({ name: "", email: "", subject: "Trade Scanner question", message: "" });
    } catch (err) {
      setContactStatus(err?.response?.data?.error || "Could not send message. Please email support directly.");
    }
  };

  return (
    <div className="site-shell ts-scan-shell">
      <style>{`
        .site-shell{min-height:100vh;color:var(--ts-text);font-family:'IBM Plex Mono',monospace;position:relative;overflow:hidden}
        .site-shell *{box-sizing:border-box;min-width:0}
        .ticker{position:relative;z-index:2;display:flex;gap:28px;overflow:hidden;border-bottom:1px solid rgba(27,49,84,.82);background:rgba(5,8,20,.86);padding:7px 0;white-space:nowrap}
        .ticker-track{display:flex;gap:28px;animation:ticker 34s linear infinite}.ticker span{font-size:10px;color:var(--ts-muted)}.ticker strong{color:var(--ts-text)}@keyframes ticker{from{transform:translateX(0)}to{transform:translateX(-50%)}}
        .nav{position:sticky;top:0;z-index:20;display:flex;align-items:center;justify-content:space-between;gap:20px;padding:15px clamp(16px,4vw,56px);background:rgba(5,8,20,.82);backdrop-filter:blur(18px);border-bottom:1px solid rgba(27,49,84,.82)}
        .brand{display:flex;align-items:center;gap:11px;font-family:'Space Grotesk',sans-serif;font-weight:900;font-size:18px;letter-spacing:0}.mark{width:28px;height:28px;border:1px solid rgba(56,189,248,.55);border-radius:8px;background:#071123;position:relative;box-shadow:0 0 24px rgba(56,189,248,.08)}.mark:before{content:'';position:absolute;inset:7px;border:1px solid var(--ts-green);border-radius:50%}.mark:after{content:'';position:absolute;left:14px;top:5px;width:1px;height:18px;background:var(--ts-blue);transform:rotate(45deg);box-shadow:0 0 12px var(--ts-blue)}
        .nav-links{display:flex;align-items:center;gap:18px}.nav-links a{color:var(--ts-muted);text-decoration:none;font-size:10px}.nav-links a:hover{color:var(--ts-blue)}.nav-actions{display:flex;gap:10px;align-items:center}
        .btn{border:0;border-radius:8px;padding:11px 15px;font-family:'IBM Plex Mono',monospace;font-weight:900;font-size:10px;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;justify-content:center;transition:transform .15s,filter .15s,border-color .15s}.btn:hover{transform:translateY(-1px);filter:brightness(1.12)}.btn.primary{background:linear-gradient(135deg,var(--ts-green),var(--ts-blue));color:#020814}.btn.ghost{background:rgba(13,23,40,.82);border:1px solid var(--ts-line);color:var(--ts-text)}
        .hero{position:relative;z-index:1;display:grid;grid-template-columns:minmax(0,1fr) minmax(300px,.76fr);gap:36px;align-items:center;padding:70px clamp(16px,5vw,72px) 56px;max-width:1240px;margin:0 auto}.eyebrow{display:inline-flex;align-items:center;gap:9px;color:var(--ts-blue);font-size:10px;letter-spacing:2px;text-transform:uppercase;border:1px solid rgba(56,189,248,.32);background:rgba(56,189,248,.06);border-radius:999px;padding:7px 11px;margin-bottom:20px}
        h1{font-family:'Space Grotesk',sans-serif;font-size:clamp(42px,6vw,76px);line-height:.94;margin:0 0 22px;letter-spacing:0}.hero p{font-size:14px;line-height:1.85;color:var(--ts-muted);max-width:690px;margin:0 0 28px}.hero-actions{display:flex;gap:12px;flex-wrap:wrap}.hero-note{font-size:10px;color:var(--ts-muted-2);margin-top:16px;line-height:1.7}
        .hero-core{min-height:390px;border-radius:14px;padding:18px;position:relative;overflow:hidden;background:linear-gradient(180deg,rgba(13,23,40,.94),rgba(7,13,28,.96));border:1px solid rgba(27,49,84,.96);box-shadow:0 30px 90px rgba(0,0,0,.36)}
        .hero-core:before{content:'';position:absolute;inset:0;background:linear-gradient(rgba(56,189,248,.045) 1px,transparent 1px),linear-gradient(90deg,rgba(56,189,248,.045) 1px,transparent 1px);background-size:34px 34px;opacity:.72}.hero-core:after{content:'';position:absolute;left:0;right:0;top:0;height:34%;background:linear-gradient(180deg,rgba(0,230,118,.12),transparent);animation:panelSweep 4.8s linear infinite}@keyframes panelSweep{0%{transform:translateY(-115%)}100%{transform:translateY(340%)}}
        .scan-ring{position:absolute;width:250px;height:250px;border-radius:50%;border:1px solid rgba(56,189,248,.24);right:-38px;top:-42px}.scan-ring:before,.scan-ring:after{content:'';position:absolute;border-radius:50%;inset:38px;border:1px solid rgba(0,230,118,.16)}.scan-ring:after{inset:78px}.scan-arm{position:absolute;right:86px;top:82px;width:110px;height:1px;background:linear-gradient(90deg,var(--ts-blue),transparent);transform-origin:right center;animation:sweep 4s linear infinite}@keyframes sweep{to{transform:rotate(360deg)}}
        .mock-card{position:relative;z-index:1;background:rgba(10,16,32,.8);border:1px solid rgba(27,49,84,.9);border-radius:10px;padding:14px;margin-bottom:12px}.mock-row{display:grid;grid-template-columns:60px 1fr 48px 54px;gap:10px;align-items:center;padding:10px 0;border-top:1px solid rgba(27,49,84,.7);font-size:10px}.bar{height:6px;border-radius:99px;background:rgba(56,189,248,.16);overflow:hidden}.bar i{display:block;height:100%;background:linear-gradient(90deg,var(--ts-green),var(--ts-blue));border-radius:99px}.up{color:var(--ts-green)}.down{color:var(--ts-red)}
        section{position:relative;z-index:1;padding:60px clamp(16px,5vw,72px);max-width:1240px;margin:0 auto}.section-title{max-width:740px;margin-bottom:26px}.section-title span{color:var(--ts-blue);font-size:10px;letter-spacing:2px;text-transform:uppercase}.section-title h2{font-family:'Space Grotesk',sans-serif;font-size:clamp(28px,4vw,44px);line-height:1.05;margin:9px 0 10px}.section-title p{color:var(--ts-muted);font-size:13px;line-height:1.8;margin:0}
        .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}.card{background:linear-gradient(180deg,rgba(13,23,40,.94),rgba(10,16,32,.94));border:1px solid rgba(27,49,84,.9);border-radius:10px;padding:19px;box-shadow:0 16px 60px rgba(0,0,0,.22)}.card h3{font-family:'Space Grotesk',sans-serif;font-size:18px;margin:0 0 8px}.card p,.card li{color:var(--ts-muted);font-size:11px;line-height:1.75}.card ul{margin:12px 0 0;padding-left:18px}.steps{counter-reset:step;display:grid;grid-template-columns:repeat(4,1fr);gap:12px}.step:before{counter-increment:step;content:'0' counter(step);display:inline-flex;color:var(--ts-green);font-weight:900;margin-bottom:18px}
        .pricing{display:grid;grid-template-columns:minmax(0,.9fr) minmax(280px,.45fr);gap:18px}.price{font-family:'Space Grotesk',sans-serif;font-size:52px;font-weight:900}.price span{font-size:14px;color:var(--ts-muted);font-family:'IBM Plex Mono',monospace}.faq details{background:rgba(13,23,40,.92);border:1px solid var(--ts-line);border-radius:10px;padding:15px 17px;margin-bottom:10px}.faq summary{cursor:pointer;font-weight:800}.faq p{color:var(--ts-muted);font-size:11px;line-height:1.8}
        .contact-wrap{display:grid;grid-template-columns:minmax(0,.7fr) minmax(280px,.5fr);gap:18px}.field{display:flex;flex-direction:column;gap:6px;margin-bottom:11px}.field label{font-size:9px;color:var(--ts-muted);letter-spacing:1px}.field input,.field textarea{background:#081225;border:1px solid var(--ts-line);border-radius:8px;padding:11px 12px;color:var(--ts-text);font-family:'IBM Plex Mono',monospace;font-size:11px}.field textarea{min-height:126px;resize:vertical}
        footer{position:relative;z-index:1;border-top:1px solid var(--ts-line);padding:26px clamp(16px,5vw,72px);color:var(--ts-muted);font-size:10px;display:flex;justify-content:space-between;gap:18px;flex-wrap:wrap;background:rgba(5,8,20,.72)}footer a{color:var(--ts-blue)}
        @media(max-width:900px){.nav{align-items:flex-start}.nav-links{display:none}.hero,.pricing,.contact-wrap{grid-template-columns:1fr}.hero{padding-top:42px}.hero-core{min-height:320px}.grid,.steps{grid-template-columns:1fr}.nav-actions .ghost{display:none}.mock-row{grid-template-columns:54px 1fr 42px 48px}.card{padding:17px}section{padding-top:48px;padding-bottom:48px}h1{font-size:clamp(38px,14vw,56px)}}
      `}</style>

      <div className="ticker" aria-label="Market ticker">
        <div className="ticker-track">
          {[...tickers, ...tickers].map((ticker, index) => (
            <span key={`${ticker.sym}-${index}`}>
              <strong>{ticker.sym}</strong> {ticker.price} {ticker.chg != null && <b className={ticker.chg >= 0 ? "up" : "down"}>{ticker.chg >= 0 ? "+" : ""}{ticker.chg.toFixed(2)}%</b>}
            </span>
          ))}
        </div>
      </div>

      <nav className="nav">
        <div className="brand"><span className="mark" aria-hidden="true" /><span className="ts-gradient-text">Trade Scanner</span></div>
        <div className="nav-links">
          <a href="#features">Features</a>
          <a href="#how">How it works</a>
          <a href="#faq">FAQ</a>
          <a href="#contact">Contact</a>
        </div>
        <div className="nav-actions">
          <button className="btn ghost" onClick={onLogin}>Sign in</button>
          <button className="btn primary" onClick={onRegister}>Get started free</button>
        </div>
      </nav>

      <main>
        <section className="hero">
          <div>
            <span className="eyebrow"><PulseDots /> US stock and ETF scanner</span>
            <h1><span className="ts-gradient-text">Trade Scanner</span><br />finds research candidates faster.</h1>
            <p>
              Discover, rank, compare, and research US stock and ETF opportunities through a scanner-style dashboard built for beginner-friendly market research.
            </p>
            <div className="hero-actions">
              <button className="btn primary" onClick={onRegister}>Get started free</button>
              <button className="btn ghost" onClick={onLogin}>Sign in</button>
            </div>
            <div className="hero-note">Free educational research tool. Not financial advice. No profitability is promised or implied.</div>
          </div>
          <div className="hero-core" aria-label="Trade Scanner dashboard preview">
            <div className="scan-ring" />
            <div className="scan-arm" />
            <div className="mock-card">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", fontSize: 10, color: "var(--ts-muted)", letterSpacing: 2, marginBottom: 12 }}>
                <span>SCANNER CORE</span><PulseDots />
              </div>
              {[
                ["NVDA", "Momentum expansion", "92", "+2.4%"],
                ["SPY", "Market benchmark", "81", "+0.6%"],
                ["MSFT", "Trend strength", "78", "+1.1%"],
                ["TSLA", "Volatility watch", "64", "-0.8%"],
              ].map(([sym, reason, score, move], i) => (
                <div className="mock-row" key={sym}>
                  <strong>{sym}</strong>
                  <div><div>{reason}</div><div className="bar"><i style={{ width: `${92 - i * 12}%` }} /></div></div>
                  <b>{score}</b>
                  <span className={move.startsWith("+") ? "up" : "down"}>{move}</span>
                </div>
              ))}
            </div>
            <div className="mock-card">
              <div style={{ fontSize: 10, color: "var(--ts-blue)", marginBottom: 8 }}>Why it surfaced</div>
              <p style={{ color: "var(--ts-muted)", fontSize: 11, lineHeight: 1.7, margin: 0 }}>
                Strong 5-day trend, price above key moving averages, improving relative strength, and clean risk/reward levels for research.
              </p>
            </div>
          </div>
        </section>

        <section id="features">
          <SectionTitle eyebrow="Value proposition" title="A scanner-style workflow for stock ideas" copy="Start from ranked candidates, then dig into context, reasons, watchlists, and research links." />
          <div className="grid">{features.map(([title, copy]) => <div className="card" key={title}><h3>{title}</h3><p>{copy}</p></div>)}</div>
        </section>

        <section>
          <SectionTitle eyebrow="Who this is for" title="Built for structure, not hype" copy="V1 is for US market traders who want a repeatable research process without copy trading, profit promises, or broker connection requirements." />
          <div className="grid">
            <div className="card"><h3>Active learners</h3><p>Use the scanner to learn what stronger and weaker setups look like across sectors.</p></div>
            <div className="card"><h3>Swing researchers</h3><p>Compare momentum, trend, movement, risk/reward, and reasons before researching deeper.</p></div>
            <div className="card"><h3>Busy traders</h3><p>Save time by starting from an organized ranked list instead of opening random charts one by one.</p></div>
          </div>
        </section>

        <section>
          <SectionTitle eyebrow="What's included" title="Everything, free" />
          <div className="card"><ul style={{ columns: 2 }}>{included.map((item) => <li key={item}>{item}</li>)}</ul></div>
        </section>

        <section id="how">
          <SectionTitle eyebrow="Process" title="How it works" />
          <div className="steps">
            {["Create a free account.", "Sign in — no payment required.", "Run the scanner and review ranked candidates.", "Save ideas, research deeper, and track what happens."].map((text) => (
              <div className="card step" key={text}><p>{text}</p></div>
            ))}
          </div>
        </section>

        <section id="faq" className="faq">
          <SectionTitle eyebrow="FAQ" title="Clear answers before you subscribe" />
          {faqs.map(([question, answer]) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}
        </section>

        <section id="contact">
          <SectionTitle eyebrow="Contact" title="Questions, support, and launch inquiries" copy={`Use the form or email ${SUPPORT_EMAIL}.`} />
          <div className="contact-wrap">
            <form className="card" onSubmit={submitContact}>
              <div className="field"><label>Name</label><input value={contact.name} onChange={(e) => setContact((p) => ({ ...p, name: e.target.value }))} /></div>
              <div className="field"><label>Email</label><input required type="email" value={contact.email} onChange={(e) => setContact((p) => ({ ...p, email: e.target.value }))} /></div>
              <div className="field"><label>Subject</label><input value={contact.subject} onChange={(e) => setContact((p) => ({ ...p, subject: e.target.value }))} /></div>
              <div className="field"><label>Message</label><textarea required value={contact.message} onChange={(e) => setContact((p) => ({ ...p, message: e.target.value }))} /></div>
              <button className="btn primary" type="submit">Send message</button>
              {contactStatus && <p style={{ color: "var(--ts-muted)", fontSize: 10 }}>{contactStatus}</p>}
            </form>
            <div className="card">
              <h3>Support email</h3>
              <p><a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "var(--ts-blue)" }}>{SUPPORT_EMAIL}</a></p>
              <p>The support email is configurable for launch so it can later move to support@domain.com without code changes.</p>
            </div>
          </div>
        </section>

        <section id="legal">
          <SectionTitle eyebrow="Legal" title="Privacy, terms, and risk disclaimer" />
          <div className="grid">
            <div className="card"><h3>Privacy Policy</h3><p>Trade Scanner collects account, billing, usage, analytics, support, and security data needed to operate the service.</p></div>
            <div className="card"><h3>Terms of Service</h3><p>Users are responsible for account activity, subscription payments, and how they use research information.</p></div>
            <div className="card"><h3>Risk Disclaimer</h3><p>Trading and investing involve risk of loss. Trade Scanner does not provide financial advice or guarantee outcomes.</p></div>
          </div>
        </section>
      </main>

      <footer>
        <div>Trade Scanner. US stocks and ETFs only for V1.</div>
        <div><a href="#legal">Privacy Policy</a> · <a href="#legal">Terms</a> · <a href="#legal">Risk Disclaimer</a> · <a href="#contact">Contact</a></div>
      </footer>
    </div>
  );
}
